/**
 * Lời gọi API cho quản trị người dùng/nhóm quyền (MH-34, MH-35) và hồ sơ cá
 * nhân/bảo mật (MH-40, MH-41) — đợt 3 phần C4. Khớp đúng các điểm cuối của
 * `apps/api/src/nguoi-dung/nguoi-dung.controller.ts` và `apps/api/src/iam/iam.controller.ts`.
 * Các đường dẫn này CHƯA có trong `@htttn/contracts` (sinh từ OpenAPI) nên dùng
 * `goi()`/`chuoiTruyVan()` dùng chung, cùng khuôn với `bao-cao-api.ts` của C2.
 */
import { goi, chuoiTruyVan, LoiApi } from './goi-chung';
import type { HoSoNguoiDung } from './khach-api';

export { LoiApi };

export interface TrangDuLieu<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/* ------------------------------- Người dùng ------------------------------- */

export interface DongNguoiDung {
  id: string;
  tenDangNhap: string;
  hoTen: string;
  email: string | null;
  dienThoai: string | null;
  /** Hợp đồng A0 hồ sơ – thông báo mục 4; vắng khi API cũ. */
  diaChi?: string | null;
  trangThai: string;
  batBuoc2fa: boolean;
  dangNhapGanNhat: string | null;
  nhom: { id: string; ten: string }[];
  phamViDiaBan: string[];
  /** Hợp đồng A0 tài khoản xã mục 4 — tuỳ chọn để không vỡ khi API cũ. */
  cap?: CapTaiKhoan;
  tenPhamVi?: string[];
  phaiDoiMatKhau?: boolean;
}

export type CapTaiKhoan = 'tinh' | 'xa';

export interface TuyChonDanhSachNguoiDung {
  page?: number;
  pageSize?: number;
  tuKhoa?: string;
  trangThai?: string;
  nhomId?: string;
  cap?: CapTaiKhoan;
  maDiaBan?: string;
}

/** Thân tạo tài khoản (POST /users). Không gửi `matKhau` → máy chủ tự sinh, trả một lần. */
export interface ThanTaoNguoiDung {
  tenDangNhap: string;
  hoTen: string;
  email?: string;
  dienThoai?: string;
  diaChi?: string;
  matKhau?: string;
  cap?: CapTaiKhoan;
  nhomId?: string[];
  phamViDiaBan?: string[];
}

/**
 * Thân sửa tài khoản (PATCH /users/:id). Máy chủ kiểm trên trạng thái sau cùng.
 * `email`/`dienThoai`/`diaChi` là `''` = xoá giá trị; vắng = giữ nguyên.
 */
export interface ThanSuaNguoiDung {
  hoTen?: string;
  email?: string;
  dienThoai?: string;
  diaChi?: string;
  cap?: CapTaiKhoan;
  nhomId?: string[];
  phamViDiaBan?: string[];
}

/** Thân PATCH /auth/me — hợp đồng A0 hồ sơ – thông báo mục 4. */
export interface ThanSuaHoSo {
  hoTen?: string;
  email?: string;
  dienThoai?: string;
  diaChi?: string;
}

export interface DongQuyen {
  ma: string;
  nhom: string;
  moTa: string;
  nguyHiem: boolean;
}

export interface DongNhom {
  id: string;
  ten: string;
  moTa: string | null;
  laMacDinh: boolean;
  soNguoiDung: number;
  quyen: string[];
  /** Nhóm không chứa quyền chỉ-cấp-tỉnh nào → gán được cho tài khoản xã. */
  dungChoCapXa?: boolean;
}

export interface KetQuaDoiQuyenNhom {
  apDung: boolean;
  soNguoiDungBiAnhHuong: number;
  quyenThem: string[];
  quyenBot: string[];
  coQuyenNguyHiem: boolean;
}

export interface DongPhien {
  id: string;
  diaChiIp: string | null;
  userAgent: string | null;
  taoLuc: string;
  hoatDongGanNhat: string;
  hetHanLuc: string;
  laPhienHienTai: boolean;
}

export const quanTriApi = {
  danhSachNguoiDung(
    tc: TuyChonDanhSachNguoiDung = {},
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<DongNguoiDung>> {
    return goi(`/api/v1/users${chuoiTruyVan({ ...tc })}`, { signal });
  },
  chiTietNguoiDung(id: string, signal?: AbortSignal): Promise<DongNguoiDung> {
    return goi(`/api/v1/users/${id}`, { signal });
  },
  taoNguoiDung(than: ThanTaoNguoiDung): Promise<{ id: string; matKhauBanDau?: string }> {
    return goi('/api/v1/users', { phuongThuc: 'POST', than });
  },
  suaNguoiDung(id: string, than: ThanSuaNguoiDung): Promise<void> {
    return goi(`/api/v1/users/${id}`, { phuongThuc: 'PATCH', than });
  },
  khoa(id: string): Promise<void> {
    return goi(`/api/v1/users/${id}/khoa`, { phuongThuc: 'POST' });
  },
  moKhoa(id: string): Promise<void> {
    return goi(`/api/v1/users/${id}/mo-khoa`, { phuongThuc: 'POST' });
  },
  datLaiMatKhau(id: string): Promise<{ matKhauMoi: string }> {
    return goi(`/api/v1/users/${id}/dat-lai-mat-khau`, { phuongThuc: 'POST' });
  },

  /* ------------------------------ Nhóm quyền ------------------------------ */
  danhSachNhom(signal?: AbortSignal): Promise<{ nhom: DongNhom[]; quyen: DongQuyen[] }> {
    return goi('/api/v1/permission-groups', { signal });
  },
  taoNhom(than: { ten: string; moTa?: string }): Promise<DongNhom> {
    return goi('/api/v1/permission-groups', { phuongThuc: 'POST', than });
  },
  doiQuyenNhom(id: string, than: { quyen: string[]; xacNhan: boolean }): Promise<KetQuaDoiQuyenNhom> {
    return goi(`/api/v1/permission-groups/${id}/quyen`, { phuongThuc: 'PATCH', than });
  },

  /* --------------------------- Hai lớp & phiên (MH-40/41) ------------------- */
  /**
   * MH-40: người dùng tự sửa hồ sơ (PATCH /auth/me). Chuỗi rỗng = xoá giá trị (trừ
   * `hoTen`). Trả hồ sơ mới để cập nhật phiên.
   */
  suaHoSo(than: ThanSuaHoSo): Promise<HoSoNguoiDung> {
    return goi('/api/v1/auth/me', { phuongThuc: 'PATCH', than });
  },
  /** MH-40: người dùng tự đổi mật khẩu; máy chủ thu hồi các phiên khác. */
  doiMatKhau(than: { matKhauHienTai: string; matKhauMoi: string }): Promise<{ daDoi: true }> {
    return goi('/api/v1/auth/doi-mat-khau', { phuongThuc: 'POST', than });
  },
  thietLap2fa(): Promise<{ secretBase32: string; otpauthUrl: string }> {
    return goi('/api/v1/auth/2fa/thiet-lap', { phuongThuc: 'POST' });
  },
  kichHoat2fa(ma: string): Promise<{ maDuPhong: string[] }> {
    return goi('/api/v1/auth/2fa/kich-hoat', { phuongThuc: 'POST', than: { ma } });
  },
  tat2fa(): Promise<void> {
    return goi('/api/v1/auth/2fa/tat', { phuongThuc: 'POST' });
  },
  danhSachPhien(signal?: AbortSignal): Promise<DongPhien[]> {
    return goi('/api/v1/auth/phien', { signal });
  },
  thuHoiPhien(id: string): Promise<void> {
    return goi(`/api/v1/auth/phien/${id}`, { phuongThuc: 'DELETE' });
  },
};

/** Bốn quyền "nguy hiểm" phải hiển thị nổi bật trong ma trận quyền (mục 1). */
export const QUYEN_NGUY_HIEM = [
  'broadcast.emergency',
  'firmware.manage',
  'platform.manage',
  'device.credential',
] as const;
